/**
 * The application's data access layer.
 *
 * Queries are described here and compiled to SQL by the API, which applies the
 * same row-level policies the database has always enforced. The builder keeps
 * the chained shape the screens were already written against, so moving off
 * the previous hosted backend did not mean rewriting every call site.
 */

export interface QueryError {
  message: string;
  code?: string;
  details?: string;
  hint?: string;
}

export interface Result<T> {
  data: T | null;
  error: QueryError | null;
  count: number | null;
}

type Filter =
  | { op: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'ilike'; column: string; value: unknown }
  | { op: 'in'; column: string; value: unknown[] }
  | { op: 'is'; column: string; value: null | boolean }
  | { op: 'not'; column: string; operator: string; value: unknown }
  | { op: 'or'; expression: string };

interface OrderTerm {
  column: string;
  ascending?: boolean;
  nullsFirst?: boolean;
  foreignTable?: string;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

interface Envelope<T> {
  data?: T;
  count?: number | null;
  error?: QueryError;
}

/**
 * One request, two response shapes.
 *
 * The query endpoints answer with `{ data, count }` because a result set needs
 * somewhere to put its metadata. The auth and administrative endpoints answer
 * with their own object -- `{ user }`, `{ message }` -- and have no metadata to
 * carry. `unwrap` says which of the two is being called.
 */
async function request<T>(path: string, init: RequestInit, unwrap: boolean): Promise<Result<T>> {
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      ...init,
      // Sessions live in an httpOnly cookie, so the browser attaches them and
      // no token is ever readable from JavaScript.
      credentials: 'same-origin',
    });

    const payload = await response.json().catch(() => null) as Envelope<T> | null;

    if (!response.ok) {
      return {
        data: null,
        count: null,
        error: payload?.error ?? {
          message: `طلب غير ناجح (${response.status})`,
          code: String(response.status),
        },
      };
    }

    return {
      data: (unwrap ? payload?.data ?? null : payload ?? null) as T,
      error: null,
      count: payload?.count ?? null,
    };
  } catch (cause) {
    // A dropped connection, a blocked request, an offline browser. Callers
    // recognise this shape and show their network message.
    return {
      data: null,
      count: null,
      error: {
        message: cause instanceof Error ? cause.message : 'فشل الاتصال بالخادم',
        code: 'network_error',
      },
    };
  }
}

function send<T>(path: string, body: unknown): Promise<Result<T>> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }, true);
}

/** `specialties(main_speciality)` -> ordering by an embedded resource. */
function parseOrderColumn(column: string): { column: string; foreignTable?: string } {
  const match = /^([A-Za-z_][A-Za-z0-9_]*)\s*\(\s*([A-Za-z_][A-Za-z0-9_]*)\s*\)$/.exec(column.trim());
  return match ? { column: match[2], foreignTable: match[1] } : { column: column.trim() };
}

class SelectBuilder<T = any> implements PromiseLike<Result<T[]>> {
  private readonly filters: Filter[] = [];
  private readonly orderTerms: OrderTerm[] = [];
  private limitValue?: number;
  private offsetValue?: number;
  private cardinality: 'many' | 'one' | 'maybe' = 'many';
  private wantCount = false;

  constructor(private readonly resource: string, private readonly selection: string) {}

  eq(column: string, value: unknown): this { this.filters.push({ op: 'eq', column, value }); return this; }
  neq(column: string, value: unknown): this { this.filters.push({ op: 'neq', column, value }); return this; }
  gt(column: string, value: unknown): this { this.filters.push({ op: 'gt', column, value }); return this; }
  gte(column: string, value: unknown): this { this.filters.push({ op: 'gte', column, value }); return this; }
  lt(column: string, value: unknown): this { this.filters.push({ op: 'lt', column, value }); return this; }
  lte(column: string, value: unknown): this { this.filters.push({ op: 'lte', column, value }); return this; }
  like(column: string, value: string): this { this.filters.push({ op: 'like', column, value }); return this; }
  ilike(column: string, value: string): this { this.filters.push({ op: 'ilike', column, value }); return this; }
  in(column: string, values: unknown[]): this { this.filters.push({ op: 'in', column, value: values }); return this; }
  is(column: string, value: null | boolean): this { this.filters.push({ op: 'is', column, value }); return this; }
  not(column: string, operator: string, value: unknown): this {
    this.filters.push({ op: 'not', column, operator, value });
    return this;
  }
  or(expression: string): this { this.filters.push({ op: 'or', expression }); return this; }

  order(column: string, options?: { ascending?: boolean; nullsFirst?: boolean }): this {
    this.orderTerms.push({ ...parseOrderColumn(column), ...options });
    return this;
  }

  limit(count: number): this { this.limitValue = count; return this; }
  range(from: number, to: number): this {
    this.offsetValue = from;
    this.limitValue = to - from + 1;
    return this;
  }

  single(): PromiseLike<Result<T>> {
    this.cardinality = 'one';
    return this as unknown as PromiseLike<Result<T>>;
  }

  maybeSingle(): PromiseLike<Result<T>> {
    this.cardinality = 'maybe';
    return this as unknown as PromiseLike<Result<T>>;
  }

  withCount(): this { this.wantCount = true; return this; }

  private run(): Promise<Result<T[]>> {
    return send<T[]>('/api/data/query', {
      resource: this.resource,
      select: this.selection,
      filters: this.filters,
      order: this.orderTerms,
      limit: this.limitValue,
      offset: this.offsetValue,
      cardinality: this.cardinality,
      count: this.wantCount,
    });
  }

  then<R1 = Result<T[]>, R2 = never>(
    onfulfilled?: ((value: Result<T[]>) => R1 | PromiseLike<R1>) | null,
    onrejected?: ((reason: unknown) => R2 | PromiseLike<R2>) | null,
  ): PromiseLike<R1 | R2> {
    return this.run().then(onfulfilled, onrejected);
  }
}

class MutationBuilder<T = any> implements PromiseLike<Result<T[]>> {
  private readonly filters: Filter[] = [];
  private returning?: string;

  constructor(
    private readonly resource: string,
    private readonly op: 'insert' | 'update' | 'upsert',
    private readonly values: Record<string, unknown> | Record<string, unknown>[],
    private readonly onConflict?: string,
  ) {}

  eq(column: string, value: unknown): this { this.filters.push({ op: 'eq', column, value }); return this; }
  neq(column: string, value: unknown): this { this.filters.push({ op: 'neq', column, value }); return this; }
  in(column: string, values: unknown[]): this { this.filters.push({ op: 'in', column, value: values }); return this; }
  is(column: string, value: null | boolean): this { this.filters.push({ op: 'is', column, value }); return this; }

  select(columns = '*'): this { this.returning = columns; return this; }

  private run(): Promise<Result<T[]>> {
    return send<T[]>('/api/data/mutate', {
      resource: this.resource,
      op: this.op,
      values: this.values,
      onConflict: this.onConflict,
      filters: this.filters,
      returning: this.returning,
    });
  }

  then<R1 = Result<T[]>, R2 = never>(
    onfulfilled?: ((value: Result<T[]>) => R1 | PromiseLike<R1>) | null,
    onrejected?: ((reason: unknown) => R2 | PromiseLike<R2>) | null,
  ): PromiseLike<R1 | R2> {
    return this.run().then(onfulfilled, onrejected);
  }
}

class Resource {
  constructor(private readonly name: string) {}

  select<T = any>(columns = '*'): SelectBuilder<T> {
    return new SelectBuilder<T>(this.name, columns);
  }

  insert<T = any>(
    values: Record<string, unknown> | Record<string, unknown>[],
  ): MutationBuilder<T> {
    return new MutationBuilder<T>(this.name, 'insert', values);
  }

  update<T = any>(values: Record<string, unknown>): MutationBuilder<T> {
    return new MutationBuilder<T>(this.name, 'update', values);
  }

  upsert<T = any>(
    values: Record<string, unknown> | Record<string, unknown>[],
    options?: { onConflict?: string },
  ): MutationBuilder<T> {
    return new MutationBuilder<T>(this.name, 'upsert', values, options?.onConflict);
  }
}

export const db = {
  from(resource: string): Resource {
    return new Resource(resource);
  },

  /** Calls a database function by name with named arguments. */
  rpc<T = any>(name: string, args: Record<string, unknown> = {}): Promise<Result<T>> {
    return send<T>(`/api/rpc/${encodeURIComponent(name)}`, args);
  },
};

/** For the endpoints that answer with their own object rather than a result set. */
export function apiPost<T = any>(path: string, body?: unknown): Promise<Result<T>> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  }, false);
}

export function apiGet<T = any>(path: string): Promise<Result<T>> {
  return request<T>(path, { method: 'GET' }, false);
}
